import { useEffect, useMemo, useState } from 'react';
import {
  useAdminEmiPayments,
  useUnifiedCustomers,
  readFinanceRecords,
  readHandLoanRecords,
  WORKSPACE_DATA_EVENT,
} from './workspaceStore';
import { useConsultancyBundle, CONSULTANCY_DATA_EVENT } from './consultancyStore';
import { useAccountingBundle, ACCOUNTING_DATA_EVENT } from './accountingStore';
import { useBooksOpening } from './booksOpeningStore';
import { computeFinancePeriodMetrics, computePortfolioTotals } from './portfolioMetrics';
import { computeLiveCashBank } from './paymentFlows';
import { getLedgerList } from './ledger';
import { daysBetweenDueAndPaid } from './overdue';
import { readWorkspaceAsOfDate, todayYmd, normalizeWorkingDate, WORKSPACE_AS_OF_EVENT } from './workspaceAsOf';

const inr = (n: number) => `₹${Math.round(Math.max(0, Number(n) || 0)).toLocaleString('en-IN')}`;

function dueYmd(value: string): string {
  if (!value || value === '—') return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function monthStartYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export type ModuleHubStats = {
  borrowers: number;
  outstanding: string;
  collectedThisMonth: string;
  overdueCount: number;
  emiDueCount: number;
  financeCount: number;
  handLoanCount: number;
  cashInHand: string;
  bankBalance: string;
  expenseCount: number;
  employeeCount: number;
  vehiclesAvailable: number;
  vehiclesSold: number;
  ledgerCount: number;
  netProfitHint: string;
};

export function useModuleHubStats(): ModuleHubStats {
  const customers = useUnifiedCustomers();
  const payments = useAdminEmiPayments();
  const [consult] = useConsultancyBundle();
  const [acct] = useAccountingBundle();
  const [books] = useBooksOpening();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const bump = () => setTick((x) => x + 1);
    window.addEventListener(WORKSPACE_DATA_EVENT, bump);
    window.addEventListener(CONSULTANCY_DATA_EVENT, bump);
    window.addEventListener(ACCOUNTING_DATA_EVENT, bump);
    window.addEventListener(WORKSPACE_AS_OF_EVENT, bump);
    return () => {
      window.removeEventListener(WORKSPACE_DATA_EVENT, bump);
      window.removeEventListener(CONSULTANCY_DATA_EVENT, bump);
      window.removeEventListener(ACCOUNTING_DATA_EVENT, bump);
      window.removeEventListener(WORKSPACE_AS_OF_EVENT, bump);
    };
  }, []);

  return useMemo(() => {
    void tick;
    const portfolio = computePortfolioTotals(customers);
    const liveCashBank = computeLiveCashBank(books, payments);
    const workingDate = normalizeWorkingDate(readWorkspaceAsOfDate() || todayYmd());
    const monthRange = { start: new Date(`${monthStartYmd()}T12:00:00`), end: new Date(`${todayYmd()}T23:59:59`) };
    const monthMetrics = computeFinancePeriodMetrics(payments, monthRange);

    let overdueCount = 0;
    let emiDueCount = 0;
    for (const c of customers) {
      const due = dueYmd(c.pendingEmi?.dueDate ?? '');
      const amount = c.pendingEmi?.amount ?? 0;
      if (!due || amount <= 0) continue;
      emiDueCount += 1;
      if (daysBetweenDueAndPaid(due, workingDate) > 0) overdueCount += 1;
    }

    const vehiclesAvailable = (consult.vehicles || []).filter((v) => v.status === 'available').length;
    const vehiclesSold = (consult.vehicles || []).filter((v) => v.status === 'sold').length;
    const ytdInterest = Math.max(0, Number(books.balances.ytdInterestIncome) || 0);
    const ytdExpenses = Math.max(0, Number(books.balances.ytdExpenses) || 0);
    const yearMetrics = computeFinancePeriodMetrics(payments, { start: null, end: null }, ytdInterest);
    const expenseTotal = acct.expenses.reduce((s, e) => s + (e.amount || 0), 0) + ytdExpenses;
    const netApprox = yearMetrics.revenue - expenseTotal;

    return {
      borrowers: portfolio.borrowers,
      outstanding: inr(portfolio.outstanding),
      collectedThisMonth: inr(monthMetrics.totalCollections),
      overdueCount,
      emiDueCount,
      financeCount: readFinanceRecords().length,
      handLoanCount: readHandLoanRecords().length,
      cashInHand: inr(liveCashBank.cash),
      bankBalance: inr(liveCashBank.bank),
      expenseCount: acct.expenses.length,
      employeeCount: acct.employees.length,
      vehiclesAvailable,
      vehiclesSold,
      ledgerCount: getLedgerList().length,
      netProfitHint: inr(netApprox),
    };
  }, [customers, payments, consult, acct, books, tick]);
}
